/**
 * This file contains utilities for using client hints for user preference which
 * are needed by the server, but are only known by the browser.
 */
import { getHintUtils } from '@epic-web/client-hints'
import { clientHint as timeZoneHint } from '@epic-web/client-hints/time-zone'
import { useEffect } from 'react'
import { useRequestInfo } from './request-info.ts'

const hintsUtils = getHintUtils({
	timeZone: timeZoneHint,
	reducedMotion: {
		cookieName: 'CH-prefers-reduced-motion',
		getValueCode: `!window.matchMedia('(prefers-reduced-motion: no-preference)').matches`,
		fallback: false,
		transform(value: string | null) {
			return value === 'true'
		},
	},
	// add other hints here
})

export const { getHints } = hintsUtils

/**
 * @returns an object with the client hints and their values
 */
export function useHints() {
	const requestInfo = useRequestInfo()
	return requestInfo.hints
}

/**
 * @returns inline script element that checks for client hints and sets cookies
 * if they are not set then reloads the page if any cookie was set to an
 * inaccurate value.
 */
export function ClientHintCheck({ nonce }: { nonce: string }) {
	useEffect(() => {
		const motionQuery = window.matchMedia(
			'(prefers-reduced-motion: no-preference)',
		)

		function handleMotionChange() {
			document.cookie = `CH-prefers-reduced-motion=${!motionQuery.matches}`
		}

		motionQuery.addEventListener('change', handleMotionChange)

		return () => {
			motionQuery.removeEventListener('change', handleMotionChange)
		}
	}, [])

	return (
		<script
			nonce={nonce}
			suppressHydrationWarning
			dangerouslySetInnerHTML={{
				__html: hintsUtils.getClientHintCheckScript(),
			}}
		/>
	)
}
